/**
 * Theme Overrides — per-theme persistence (API-backed with localStorage cache).
 *
 * Stores admin-configured token overrides per theme name.
 * ThemeProvider reads these at runtime and merges them into the
 * token map before applying to :root.
 *
 * Two levels of override:
 *   1. highlightColor — a single hex that auto-derives the full accent family
 *   2. tokenOverrides — arbitrary CSS custom property overrides (full designer)
 *
 * tokenOverrides take precedence over highlightColor-derived values.
 *
 * **Storage:** Writes to API first, then updates localStorage cache.
 * Reads always come from localStorage for speed.
 */

import { saveThemeOverrideApi } from '../api/customThemes';
import { TEXTURE_ALLOWLIST_TOKEN } from './backgroundTexture';

const LS_KEY = 'l4e_theme_overrides';

export interface ThemeOverrides {
  /** Primary highlight / accent color hex, e.g. "#75E2FF". */
  highlightColor?: string;
  /** Arbitrary token overrides from the full theme designer. */
  tokenOverrides?: Record<string, string>;
}

export type AllThemeOverrides = Record<string, ThemeOverrides>;

/** Read all saved overrides (keyed by theme name). */
export function readThemeOverrides(): AllThemeOverrides {
  try {
    const raw = window?.localStorage?.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as AllThemeOverrides;
  } catch { /* ignore */ }
  return {};
}

/** Read overrides for a single theme. */
export function getOverridesForTheme(themeName: string): ThemeOverrides {
  const all = readThemeOverrides();
  return all[themeName] || {};
}

/** Save overrides for a single theme (merges with existing). Writes to API + cache. */
export function saveThemeOverrides(themeName: string, overrides: ThemeOverrides): void {
  // Update localStorage cache immediately (synchronous, for fast UI response)
  try {
    const all = readThemeOverrides();
    all[themeName] = { ...all[themeName], ...overrides };
    window?.localStorage?.setItem(LS_KEY, JSON.stringify(all));
  } catch { /* ignore */ }

  // Fire-and-forget API save
  saveThemeOverrideApi(themeName, {
    highlightColor: overrides.highlightColor,
    tokenOverrides: overrides.tokenOverrides,
  }).catch((err) => {
    console.warn('[ThemeOverrides] API save failed:', err);
  });
}

/** Save full token overrides for a theme (replaces tokenOverrides entirely). */
export function saveTokenOverrides(themeName: string, tokens: Record<string, string>): void {
  // Update localStorage cache immediately
  try {
    const all = readThemeOverrides();
    const existing = all[themeName] || {};
    all[themeName] = { ...existing, tokenOverrides: tokens };
    window?.localStorage?.setItem(LS_KEY, JSON.stringify(all));
  } catch { /* ignore */ }

  // Fire-and-forget API save
  saveThemeOverrideApi(themeName, { tokenOverrides: tokens }).catch((err) => {
    console.warn('[ThemeOverrides] API token overrides save failed:', err);
  });
}

/* ── Background allowlist (DEV-1478) ─────────────────────────────
 *
 * Which background textures a theme offers is an admin choice, stored in that
 * theme's EXISTING tokenOverrides bag under a reserved `--`-prefixed key. That
 * bag already round-trips localStorage → API → theme_overrides.token_overrides
 * (JSONB) for built-in and custom themes alike, so this needed no migration, no
 * new column and no new endpoint.
 *
 * ABSENT key  → not configured; the code defaults in backgroundTexture.ts apply.
 * PRESENT key → the admin's explicit list wins, even if empty (= Solid only).
 * That distinction is why this reads/writes the key rather than just storing an
 * array: absent and empty must not collapse into the same thing, or a theme
 * could never be reset to defaults.
 */

/** The admin-configured texture ids for a theme, or null when not configured. */
export function getTextureAllowlistRaw(themeName: string): string | null {
  const tokens = getOverridesForTheme(themeName).tokenOverrides;
  const raw = tokens?.[TEXTURE_ALLOWLIST_TOKEN];
  return raw == null ? null : String(raw);
}

/** Persist the allowlist for a theme. Pass null to clear it (revert to defaults). */
export function saveTextureAllowlist(themeName: string, raw: string | null): void {
  const existing = getOverridesForTheme(themeName).tokenOverrides || {};
  const next = { ...existing };
  if (raw == null) delete next[TEXTURE_ALLOWLIST_TOKEN];
  else next[TEXTURE_ALLOWLIST_TOKEN] = raw;
  saveTokenOverrides(themeName, next);
}

/** Clear all overrides for a theme (reset to defaults). */
export function clearThemeOverrides(themeName: string): void {
  try {
    const all = readThemeOverrides();
    delete all[themeName];
    window?.localStorage?.setItem(LS_KEY, JSON.stringify(all));
  } catch { /* ignore */ }

  // API: save empty overrides
  saveThemeOverrideApi(themeName, { tokenOverrides: {} }).catch((err) => {
    console.warn('[ThemeOverrides] API clear failed:', err);
  });
}

/**
 * Given a hex color like "#75E2FF", derive the set of CSS custom
 * property overrides for the accent / highlight family.
 */
export function deriveHighlightTokens(hex: string): Record<string, string> {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Darken by ~15% for hover
  const hoverR = Math.round(r * 0.85);
  const hoverG = Math.round(g * 0.85);
  const hoverB = Math.round(b * 0.85);
  const hoverHex = `#${[hoverR, hoverG, hoverB].map((c) => c.toString(16).padStart(2, '0')).join('')}`;

  return {
    '--accent-primary':       hex,
    '--accent-primary-hover':  hoverHex,
    '--accent-bg':            `rgba(${r}, ${g}, ${b}, 0.16)`,
    '--accent-border':        `rgba(${r}, ${g}, ${b}, 0.35)`,
    '--accent-text':          hex,
    '--accent-bg-40':         `rgba(${r}, ${g}, ${b}, 0.25)`,
  };
}

/**
 * Resolve the final token overrides for a theme.
 * For built-in themes: tokenOverrides > highlightColor-derived > base.
 * For custom themes: custom theme's tokenOverrides are layered on the base.
 * Returns only the override tokens (not the base theme).
 */
// Lazy-loaded themeRegistry to break circular dependency (themeOverrides ↔ themeRegistry).
// Vite does not support synchronous require() — we resolve on first call instead.
let _getCustomTheme: ((name: string) => any) | null = null;
function getCustomThemeLazy(name: string): any {
  if (!_getCustomTheme) {
    // Dynamic import would be async; since this is called synchronously from
    // ThemeProvider, we use a deferred static reference instead.
    // The themeRegistry module sets this via registerThemeLookup() at init time.
    return null;
  }
  return _getCustomTheme(name);
}

/** Called by themeRegistry at module init to break the circular dep. */
export function registerThemeLookup(fn: (name: string) => any): void {
  _getCustomTheme = fn;
}

export function resolveOverrideTokens(themeName: string): Record<string, string> {
  const custom = getCustomThemeLazy(themeName);

  if (custom) {
    // Custom theme: start from its stored overrides, then layer any live tweaks
    let result: Record<string, string> = {};
    if (custom.tokenOverrides) {
      result = { ...custom.tokenOverrides };
    }
    // Also apply any per-theme admin overrides on top
    const overrides = getOverridesForTheme(themeName);
    if (overrides.highlightColor) {
      result = { ...result, ...deriveHighlightTokens(overrides.highlightColor) };
    }
    if (overrides.tokenOverrides) {
      result = { ...result, ...overrides.tokenOverrides };
    }
    return result;
  }

  // Built-in theme: standard override resolution
  const overrides = getOverridesForTheme(themeName);
  let result: Record<string, string> = {};

  if (overrides.highlightColor) {
    result = { ...result, ...deriveHighlightTokens(overrides.highlightColor) };
  }
  if (overrides.tokenOverrides) {
    result = { ...result, ...overrides.tokenOverrides };
  }

  return result;
}
